// Client for the portal API facade's GET /overview (HOME-243). Replaces the
// Overview dashboard's client-side fan-out — one call per box's
// docker-status endpoint (statusApi.ts's now-retired fetchBoxStatus) plus
// one to convmem's queue-status (convmemApi.ts's now-retired
// fetchConvmemStatus) — with a single request to the portal API, which does
// that fan-out itself server-side over mTLS
// (infra/cdk/lambdas/portal-overview) and returns everything already shaped.
//
// Cognito-gated like the rest of the portal API (certApi.ts) — same
// Authorization: Bearer <id/access token> header, same base URL
// (Config.api.apiUrl).

import Config from '../config/config';
import type { BoxStatus } from './statusApi';
import type { ConvmemStatus } from './convmemApi';

export interface OverviewResponse {
    /** Keyed by box id (matches config/infra.ts's BOXES ids: 'luke' | 'leia' | 'cal'). */
    boxes: Record<string, BoxStatus>;
    convmem: ConvmemStatus;
    generatedAt?: string;
}

const TIMEOUT_MS = 8000;

const unreachableBoxes = (boxIds: string[], error: string): Record<string, BoxStatus> =>
    Object.fromEntries(boxIds.map(id => [id, { reachable: false, containers: {}, error } as BoxStatus]));

/**
 * Fetch the aggregated overview payload. Never throws: if the facade call
 * itself fails outright (network/timeout/auth), every box and convmem come
 * back `reachable: false` with the failure reason, the same graceful
 * degradation the old per-box browser fan-out provided — it has just moved
 * up one level, from "this box didn't answer" to "the facade didn't
 * answer".
 */
export async function fetchOverview(token: string, boxIds: string[]): Promise<OverviewResponse> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
        const base = Config.api.apiUrl.replace(/\/$/, '');
        const res = await fetch(`${base}/overview`, {
            signal: controller.signal,
            headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            const error = `${res.status} ${res.statusText}`;
            return { boxes: unreachableBoxes(boxIds, error), convmem: { reachable: false, error } };
        }
        const data = (await res.json()) as OverviewResponse;
        return {
            boxes: data.boxes ?? unreachableBoxes(boxIds, 'malformed /overview response'),
            convmem: data.convmem ?? { reachable: false, error: 'malformed /overview response' },
            generatedAt: data.generatedAt,
        };
    } catch (e) {
        const msg = e instanceof DOMException && e.name === 'AbortError'
            ? 'timed out'
            : e instanceof Error ? e.message : String(e);
        return { boxes: unreachableBoxes(boxIds, msg), convmem: { reachable: false, error: msg } };
    } finally {
        clearTimeout(timer);
    }
}
